/**
 * PhoneInput
 * ==========
 * A phone-number field with country-code picker.
 *
 * • Country picker (searchable) with flag emoji + dial code
 * • Free-form phone input — user can type anything (no forced validation)
 * • On change, emits { country: ISO2, dialCode: "+91", national: "9812345678", e164: "+919812345678" }
 * • Best-effort E.164 formatting via libphonenumber-js when a country is set;
 *   otherwise stores the raw string so half-typed / unusual numbers still save.
 *
 * Auto-selects a country when the parent passes `defaultCountry` (e.g. from
 * the lead's `country` field), otherwise falls back to India.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js/min";
import { Search, ChevronDown, X } from "lucide-react";

// ISO country name lookup — libphonenumber only ships ISO2 codes.
// We keep a small display-name table so the picker isn't just "IN, US, GB".
// Falls back to the ISO code itself if we don't have a friendly name.
const COUNTRY_NAMES = {
  IN: "India", US: "United States", GB: "United Kingdom", AE: "UAE",
  SA: "Saudi Arabia", KW: "Kuwait", ZA: "South Africa", KE: "Kenya",
  NG: "Nigeria", EG: "Egypt", MA: "Morocco", SG: "Singapore",
  AU: "Australia", CA: "Canada", DE: "Germany", FR: "France",
  IT: "Italy", ES: "Spain", NL: "Netherlands", CH: "Switzerland",
  SE: "Sweden", NO: "Norway", DK: "Denmark", FI: "Finland",
  IE: "Ireland", NZ: "New Zealand", BR: "Brazil", MX: "Mexico",
  AR: "Argentina", CL: "Chile", CO: "Colombia", PE: "Peru",
  JP: "Japan", KR: "South Korea", CN: "China", HK: "Hong Kong",
  TW: "Taiwan", TH: "Thailand", VN: "Vietnam", ID: "Indonesia",
  MY: "Malaysia", PH: "Philippines", PK: "Pakistan", BD: "Bangladesh",
  LK: "Sri Lanka", NP: "Nepal", RU: "Russia", TR: "Turkey",
  IL: "Israel", QA: "Qatar", OM: "Oman", BH: "Bahrain",
  JO: "Jordan", LB: "Lebanon", GH: "Ghana", TZ: "Tanzania",
  UG: "Uganda", RW: "Rwanda", ET: "Ethiopia", BE: "Belgium",
  AT: "Austria", PT: "Portugal", GR: "Greece", CZ: "Czech Republic",
  PL: "Poland", HU: "Hungary", RO: "Romania", UA: "Ukraine",
};

// Flag emoji from ISO-3166 alpha-2 (each letter → regional indicator symbol).
function flagFor(iso) {
  if (!iso || iso.length !== 2) return "🌐";
  const A = 0x1f1e6;
  const codepoints = [...iso.toUpperCase()].map((c) => A + c.charCodeAt(0) - 65);
  return String.fromCodePoint(...codepoints);
}

// Guess ISO code from a free-form country name — used to auto-pick the
// dial code when the parent passes `defaultCountry="India"`.
function countryNameToIso(name) {
  if (!name) return null;
  const norm = name.trim().toLowerCase();
  const hit = Object.entries(COUNTRY_NAMES).find(([, n]) => n.toLowerCase() === norm);
  return hit ? hit[0] : null;
}

const ALL_COUNTRIES = getCountries()
  .map((iso) => ({
    iso,
    name: COUNTRY_NAMES[iso] || iso,
    dial: `+${getCountryCallingCode(iso)}`,
    flag: flagFor(iso),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));


export default function PhoneInput({
  value = "",              // canonical string stored on the lead (E.164 or raw)
  onChange,                // (nextValue) => void — receives the E.164 or raw string
  defaultCountry = null,   // e.g. "India" — used to preselect the picker
  placeholder = "Phone",
  testId = "phone-input",
  disabled = false,
}) {
  const [country, setCountry] = useState(() => {
    // Try parsing an existing value first (preserves the user's choice
    // when editing an already-saved lead).
    if (value) {
      const parsed = parsePhoneNumberFromString(String(value));
      if (parsed?.country) return parsed.country;
    }
    return countryNameToIso(defaultCountry) || "IN";
  });
  const [national, setNational] = useState(() => {
    if (value) {
      const parsed = parsePhoneNumberFromString(String(value));
      if (parsed?.nationalNumber) return parsed.nationalNumber;
    }
    // If there's a raw value without country, keep it as-is (strip leading + and
    // any known dial code we can guess later — for now leave for the user).
    return String(value || "").replace(/^\+\d{1,4}/, "").trim();
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  // Sync back when parent country prop changes (e.g. user edits `country`
  // field in the same form).
  useEffect(() => {
    if (!value && defaultCountry) {
      const iso = countryNameToIso(defaultCountry);
      if (iso) setCountry(iso);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountry]);

  // Close the dropdown on outside-click
  useEffect(() => {
    if (!pickerOpen) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [pickerOpen]);

  const currentCountry = useMemo(
    () => ALL_COUNTRIES.find((c) => c.iso === country) || ALL_COUNTRIES[0],
    [country],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return ALL_COUNTRIES;
    const q = query.toLowerCase();
    return ALL_COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.iso.toLowerCase().includes(q),
    );
  }, [query]);

  const emit = (nextCountry, nextNational) => {
    const raw = (nextNational || "").trim();
    if (!raw) {
      onChange && onChange("");
      return;
    }
    // Best-effort formatting; on failure just concat.
    const parsed = parsePhoneNumberFromString(raw, nextCountry);
    if (parsed?.number) {
      onChange && onChange(parsed.number);
    } else {
      onChange && onChange(`+${getCountryCallingCode(nextCountry)}${raw.replace(/[^\d]/g, "")}`);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-stretch gap-2" data-testid={testId}>
        {/* Country picker */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setPickerOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 h-9 px-2 rounded-md border border-border bg-background text-sm hover:border-primary/50 shrink-0"
          data-testid={`${testId}-country-btn`}
        >
          <span className="text-base leading-none">{currentCountry.flag}</span>
          <span className="tabular-nums text-xs text-muted-foreground">{currentCountry.dial}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>

        {/* Phone input — free-form */}
        <input
          type="tel"
          value={national}
          disabled={disabled}
          onChange={(e) => {
            setNational(e.target.value);
            emit(country, e.target.value);
          }}
          placeholder={placeholder}
          data-testid={`${testId}-field`}
          className="flex-1 h-9 rounded-md border border-border bg-background px-3 text-sm"
        />
      </div>

      {pickerOpen && (
        <div className="absolute z-50 mt-1 w-[280px] rounded-md border border-border bg-background shadow-lg" data-testid={`${testId}-picker`}>
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or +code"
                className="w-full h-8 pl-8 pr-7 rounded border border-border bg-background text-xs"
                data-testid={`${testId}-search`}
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-secondary"
                  aria-label="Clear"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.slice(0, 60).map((c) => (
              <button
                key={c.iso}
                type="button"
                onClick={() => {
                  setCountry(c.iso);
                  setPickerOpen(false);
                  setQuery("");
                  emit(c.iso, national);
                }}
                className={
                  "flex items-center gap-2 w-full px-2 py-1.5 text-xs hover:bg-secondary text-left " +
                  (c.iso === country ? "bg-secondary/70 font-medium" : "")
                }
                data-testid={`${testId}-option-${c.iso}`}
              >
                <span className="text-base leading-none">{c.flag}</span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="tabular-nums text-muted-foreground">{c.dial}</span>
              </button>
            ))}
            {filtered.length > 60 && (
              <div className="px-2 py-1.5 text-[10px] text-muted-foreground">
                Showing 60 of {filtered.length} — refine your search
              </div>
            )}
            {filtered.length === 0 && (
              <div className="px-2 py-3 text-xs text-muted-foreground">No countries match.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
